import EmptyListIcon from '@root/public/images/shared/BlankSheet.png'
import Image from 'next/image'
import { FC } from 'react'

import { useFeedList } from '@/api/models/news/hooks/useFeedList'
import { FeedType } from '@/api/models/news/lib/feed.constants'
import { ScreenSize } from '@/app/constants'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import ChevronRightSolidIconSvg from '@/assets/svg/ChevronRightSolidIcon.svg'
import { ErrorCard, ErrorForbidden } from '@/components/pages/plugs'
import EmptyListPlug from '@/components/pages/plugs/EmptyListPlug'
import { AppButton } from '@/components/shared/button'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { FeedSummarySkeleton } from './FeedSummarySkeleton'
import NewsSummaryCard from './NewsSummaryCard'
import ProposalSummaryCard from './ProposalSummaryCard'

type Props = {
  className?: string
  isAccessForbidden: boolean
}

const PAGE_SIZE = 5

const FeedSummary: FC<Props> = ({ className, isAccessForbidden }) => {
  const {
    data: feedPages,
    isLoading,
    isError,
    error
  } = useFeedList({ pageSize: PAGE_SIZE })

  const feedData = feedPages?.flat()
  const { reachGoal } = useYandexMetrika()
  const { breakpoint } = useCurrentBreakpoint()

  const isLargeScreen = [ScreenSize.Xxl].includes(breakpoint)

  if (isForbiddenError(error) || isAccessForbidden) {
    return (
      <div
        className={cn(
          'relative flex max-h-full min-h-[150px] flex-col rounded-16 bg-white p-32 sm:p-16',
          className
        )}
      >
        <h3 className="font-bold text-28 md:text-24 sm:text-20">
          Новости и предложения
        </h3>
        <div className="h-full">
          <ErrorForbidden className="my-[121px] max-w-[1156px]" />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div
        className={cn(
          'relative flex max-h-full min-h-[150px] flex-col rounded-16 bg-white p-32 sm:p-16',
          className
        )}
      >
        <div className="flex h-full justify-center">
          <ErrorCard />
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-16 bg-white p-24 md:p-16',
        'sm:bg-transparent sm:px-0 sm:pb-0 sm:pt-20',
        feedData?.length && feedData?.length > 0 && 'sm:min-h-[700px]',
        isLargeScreen ? 'min-h-[700px]' : '',
        className
      )}
    >
      {isLoading ? (
        <FeedSummarySkeleton />
      ) : (
        <>
          <div className="mb-24 flex items-center justify-between md:mb-12">
            <h3 className="font-bold text-28 md:text-24 sm:text-20">
              Новости и предложения
            </h3>
            <AppButton
              href="/feed"
              variant="link"
              size="default"
              className="gap-x-8"
              onClick={() => reachGoal(YandexMetrikaEvent.MAIN_FEED)}
            >
              <span>Все</span>
              <ChevronRightSolidIconSvg className="h-16 w-16 stroke-bronze-40" />
            </AppButton>
          </div>
          <div
            className={cn(
              'overflow-auto',
              feedData?.length === 0 &&
                'flex h-full items-center justify-center'
            )}
          >
            {feedData?.length === 0 ? (
              <EmptyListPlug
                image={
                  <Image
                    src={EmptyListIcon.src}
                    alt="Empty list"
                    width={100}
                    height={100}
                  />
                }
                title="Пока тут пусто"
                titleClassName="text-24 xs:text-16"
                descriptionClassName="!text-grey-60 text-16 xs:text-12"
                description="Скоро здесь появятся новости и предложения"
                className="!mt-0 flex justify-center md:min-h-[360px] sm:!h-[316px]"
              />
            ) : (
              feedData?.map(feedItem =>
                feedItem.type === FeedType.News ? (
                  <NewsSummaryCard key={feedItem.id} newsItem={feedItem} />
                ) : (
                  <ProposalSummaryCard
                    key={feedItem.id}
                    proposalItem={feedItem}
                  />
                )
              )
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default FeedSummary
